import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  ogType?: "website" | "article";
}

const DOMAIN = "https://testassets.io";
const OG_IMAGE = `${DOMAIN}/icon.png`;
const SUPPORTED_LANGUAGES = ["en", "de", "es", "fr", "it", "ja", "nl", "pl", "pt", "sv", "zh"];

const SEOHead = ({ title, description, path, jsonLd, noindex, breadcrumbs, ogType = "website" }: SEOHeadProps) => {
  const { i18n } = useTranslation();
  const canonical = `${DOMAIN}${path}`;

  const breadcrumbJsonLd = breadcrumbs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: item.url.startsWith("http") ? item.url : `${DOMAIN}${item.url}`,
        })),
      }
    : null;

  return (
    <Helmet>
      <html lang={i18n.language || "en"} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang for multilingual support */}
      {SUPPORTED_LANGUAGES.map((lang) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={`${DOMAIN}${path}?lang=${lang}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="TestAssets" />
      <meta property="og:locale" content={i18n.language || "en"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {/* JSON-LD structured data */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}

      {/* Breadcrumb JSON-LD */}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
